import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, ShieldCheck, FileText } from "lucide-react";
import { useSearchParams } from "react-router-dom";

const money = (n) => Number(n ?? 0).toLocaleString("en-HK", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");
const normalizePlanName = (value) =>
    String(value || "")
        .replace(/\*/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

const EMPTY_APPLICANT = { name: "", email: "", telephone: "", address: "", notes: "" };



export default function PropertyInsurance() {
  const [options, setOptions] = useState(null);
  const [optionsError, setOptionsError] = useState("");
  const [selections, setSelections] = useState({});
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [applicant, setApplicant] = useState(EMPTY_APPLICANT);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get("deal");

  useEffect(() => {
    let cancelled = false;
    api.get("/member/hk_property_insurance/options")
      .then((r) => { if (!cancelled) setOptions(r.data); })
      .catch((e) => { if (!cancelled) setOptionsError(formatApiError(e)); });
    return () => { cancelled = true; };
  }, []);

  const complete = useMemo(
    () => Boolean(options?.fields?.length) && options.fields.every((f) => Boolean(selections[f.key])),
    [options, selections]
  );

  useEffect(() => {
    setSubmitted(null);
    setQuoteError("");
    if (!complete) {
      setQuote(null);
      setLoadingQuote(false);
      return undefined;
    }
    let cancelled = false;
    setLoadingQuote(true);
    api.post("/member/hk_property_insurance/price", { selections })
      .then((r) => { if (!cancelled) setQuote(r.data); })
      .catch((e) => { if (!cancelled) { setQuote(null); setQuoteError(formatApiError(e)); } })
      .finally(() => { if (!cancelled) setLoadingQuote(false); });
    return () => { cancelled = true; };
  }, [complete, selections]);

  const updateApplicant = (key, value) => setApplicant((a) => ({ ...a, [key]: value }));
  const validate = () => {
    const next = {};
    ["name", "email", "telephone", "address"].forEach((key) => {
      if (!String(applicant[key] || "").trim()) next[key] = "This field is required.";
    });
    if (applicant.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applicant.email)) {
      next.email = "Enter a valid email address.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!quote?.available) {
      toast.error("Please select a valid insurance option first.");
      return;
    }
    if (!validate()) {
      toast.error("Please complete the required applicant details.");
      return;
    }
    setBusy(true);
    try {
      const response = await api.post("/member/hk_property_insurance/submit", {
        selections,
        applicant,
        deal_id: dealId,
      });
      setSubmitted(response.data);
      toast.success(`Enquiry ${response.data.display_id} submitted.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

    // Plan documents, served statically from frontend/public/pdf/.
    const PLAN_DOCS = [
        { key: "Home Contents Gold Plan*", title: "Standard - MSIG", file: "/pdf/MSIG_Gold_Plan_20230324.pdf", caption: "PDF · Everyday protection for your helper" },
        { key: "Homeguard Plan  2*", title: "Elite - Allied", file: "/pdf/Allied_World_Homeguard_Plan_II_20230324.pdf", caption: "PDF · policy details" },
        { key: "ZURICH HomeChoice Plan*", title: "Premium - ZURICH", file: "/pdf/Zurich_HomeChoice_Insurance_Plan_20230324.pdf", caption: "PDF · Bigger cover, bigger peace of mind" },
    ];

    const PLAN_DOC_LOOKUP = Object.fromEntries(
        PLAN_DOCS.map((plan) => [plan.key, plan])
    );

    const header = <PageHeader eyebrow="Insurance" title="Home Contents Insurance" testid="property-insurance-header" />;
  if (optionsError) return <>{header}<div className="px-6 lg:px-12 py-8"><div className="rounded-xl border border-red-200 bg-white p-5 text-sm text-red-700">Unable to load property-insurance options: {optionsError}</div></div></>;
  if (!options) return <>{header}<div className="flex justify-center py-16"><Loader2 className="animate-spin text-navy-300" size={28} /></div></>;

  if (submitted) return <>
    {header}
    <div className="max-w-3xl px-6 lg:px-12 py-10">
      <div className="rounded-2xl border border-emerald-200 bg-white p-8">
        <CheckCircle2 className="mb-3 text-emerald-600" size={34} />
        <h2 className="text-xl font-semibold text-navy-800">Enquiry submitted</h2>
        <p className="mt-2 text-sm text-navy-600">Your reference is <strong>{submitted.display_id}</strong>.</p>
        {submitted.refer_for_quote
          ? <p className="mt-3 text-sm text-navy-700">Your selected option requires a broker quote. The broker will contact you using the details provided.</p>
          : <p className="mt-3 text-sm text-navy-700">Your enquiry has been recorded with the displayed premium.</p>}
        <Button className="mt-6" onClick={() => { setSubmitted(null); setSelections({}); setQuote(null); setApplicant(EMPTY_APPLICANT); }}>Start another enquiry</Button>
      </div>
    </div>
  </>;

  return <>
    {header}
    <div className="max-w-5xl space-y-6 px-6 py-8 lg:px-12">
      <div className="rounded-2xl border border-navy-100 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-1 shrink-0 text-gold-600" size={24} />
          <div>
                      <h2 className="text-lg font-semibold text-navy-800">Find Home Contents Insurance cover</h2>
            <p className="mt-1 text-sm leading-relaxed text-navy-600">Choose the product, plan, building type and gross floor area to check the premium or request a broker quote.</p>
          </div>
        </div>
          </div>
          {/* ---------- Plan documents ---------- */}
          <Card data-testid="helper-docs">
              <SectionTitle>Home Contents Insurance Plan options</SectionTitle>
              <p className="text-sm text-navy-500 -mt-2 mb-4">
                  Download the Home Contents Insurance plan options and see what's inside.
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                  {PLAN_DOCS.map((doc) => (
                      <a
                          key={doc.file}
                          href={doc.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          data-testid={`helper-doc-${slug(doc.title)}`}
                          className="group rounded-xl border border-navy-100 bg-white p-4 flex items-start gap-3 hover:border-gold-500 hover:shadow-soft transition-all"
                      >
                          <FileText
                              className="text-gold-600 shrink-0 mt-0.5"
                              size={20}
                          />
                          <div className="min-w-0">
                              <div className="text-sm font-semibold text-navy-700 group-hover:text-navy-900">
                                  {doc.title}
                              </div>
                              <div className="text-xs text-navy-400 mt-0.5 truncate">
                                  {doc.caption}
                              </div>
                          </div>
                      </a>
                  ))}
              </div>
          </Card>


      <section className="rounded-2xl border border-navy-100 bg-white p-6 shadow-sm">
        <h3 className="mb-2 font-semibold text-navy-800">Insurance requirements</h3>
        <p className="mb-5 text-sm text-navy-500">Available options are loaded from the current pricing records.</p>
        <div className="grid gap-5 sm:grid-cols-2">
          {options.fields.map((field) => <div key={field.key} className="space-y-2">
            <Label>{field.label} <span className="text-red-500">*</span></Label>
            <Select value={selections[field.key] || ""} onValueChange={(value) => setSelections((old) => ({ ...old, [field.key]: value }))}>
              <SelectTrigger data-testid={`property-option-${slug(field.key)}`}><SelectValue placeholder={`Select ${field.label.toLowerCase()}`} /></SelectTrigger>
                  <SelectContent>
                      {field.values
                          .filter((value) => {
                              if (field.key !== "Plan / Benefit Tier") return true;

                              return Object.keys(PLAN_DOC_LOOKUP).some(
                                  (plan) =>
                                      normalizePlanName(plan) === normalizePlanName(value)
                              );
                          })
                          .map((value) => (
                              <SelectItem key={value} value={value}>
                                  {value}
                              </SelectItem>
                          ))}
                  </SelectContent>
            </Select>
          </div>)}
        </div>
      </section>

      {complete && <section className="rounded-2xl border border-navy-100 bg-white p-6 shadow-sm" aria-live="polite">
        <h3 className="mb-4 font-semibold text-navy-800">Premium result</h3>
        {loadingQuote && <div className="flex items-center gap-2 text-sm text-navy-500"><Loader2 className="animate-spin" size={18} />Checking the pricing table…</div>}
        {!loadingQuote && quoteError && <p className="text-sm text-red-600">{quoteError}</p>}
        {!loadingQuote && quote && !quote.available && <div className="flex gap-2 text-sm text-amber-800"><AlertTriangle size={18} className="shrink-0" />{quote.reason}</div>}
        {!loadingQuote && quote?.available && quote.refer_for_quote && <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex gap-2 font-semibold text-amber-900"><AlertTriangle size={20} />Broker quote required</div>
          <p className="mt-2 text-sm text-amber-900">The selected property/plan combination needs an individual quote. Submit your details and the broker will contact you.</p>
          {quote.contact_text && <p className="mt-2 text-sm text-amber-900">{quote.contact_text}</p>}
          {quote.carrier && <p className="mt-2 text-sm text-amber-900">Insurer: {quote.carrier}</p>}
        </div>}
        {!loadingQuote && quote?.available && !quote.refer_for_quote && quote.priced && <div className="grid gap-4 sm:grid-cols-3">
          <Price label="Original premium" value={quote.original} currency={quote.currency} />
          <Price label="Discounted premium" value={quote.discounted} currency={quote.currency} highlight />
          <Price label="Savings" value={quote.savings} currency={quote.currency} />
          {quote.discount_pct != null && <p className="text-sm text-emerald-700 sm:col-span-3">Discount: {quote.discount_pct}%</p>}
          {quote.carrier && <p className="text-sm text-navy-600 sm:col-span-3">Insurer: {quote.carrier}</p>}
        </div>}
        {!loadingQuote && quote?.available && !quote.refer_for_quote && !quote.priced && <p className="text-sm text-amber-800">A premium was not returned for this option. Please contact the broker for confirmation.</p>}
      </section>}

      <section className="rounded-2xl border border-navy-100 bg-white p-6 shadow-sm">
        <h3 className="mb-2 font-semibold text-navy-800">Applicant and property details</h3>
        <p className="mb-5 text-sm text-navy-500">Your details will be included in the referral record sent to the broker.</p>
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField label="Full name" value={applicant.name} error={errors.name} onChange={(v) => updateApplicant("name", v)} required />
          <TextField label="Email address" type="email" value={applicant.email} error={errors.email} onChange={(v) => updateApplicant("email", v)} required />
          <TextField label="Telephone" value={applicant.telephone} error={errors.telephone} onChange={(v) => updateApplicant("telephone", v)} required />
          <TextField label="Property address" value={applicant.address} error={errors.address} onChange={(v) => updateApplicant("address", v)} required />
          <div className="space-y-2 sm:col-span-2"><Label>Additional notes (optional)</Label><textarea value={applicant.notes} onChange={(e) => updateApplicant("notes", e.target.value)} rows={3} maxLength={3000} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" placeholder="Anything the broker should know…" /></div>
        </div>
        <Button className="mt-6" disabled={busy || !quote?.available || loadingQuote} onClick={submit}>
          {busy ? <><Loader2 className="mr-2 animate-spin" size={16} />Submitting…</> : quote?.refer_for_quote ? "Request a broker quote" : "Submit insurance enquiry"}
        </Button>
      </section>
    </div>
  </>;
}

/* ------------------------------ atoms ------------------------------ */

function Card({ children }) {
    return (
        <div className="rounded-2xl bg-white shadow-soft p-6 border border-navy-100/60">
            {children}
        </div>
    );
}

function SectionTitle({ children }) {
    return <h2 className="font-display text-xl text-navy-700 mb-4">{children}</h2>;
}

function Price({ label, value, currency, highlight }) {
  return <div className={`rounded-xl border p-4 ${highlight ? "border-gold-300 bg-gold-50" : "border-navy-100 bg-white"}`}>
    <div className="text-xs text-navy-500">{label}</div>
    <div className="mt-1 text-xl font-semibold text-navy-800">{currency || "HKD"} {money(value)}</div>
  </div>;
}

function TextField({ label, value, onChange, error, type = "text", required }) {
  return <div className="space-y-2">
    <Label>{label}{required && <span className="text-red-500"> *</span>}</Label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={Boolean(error)} className={`w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring ${error ? "border-red-400" : "border-input"}`} />
    {error && <p className="text-xs text-red-600">{error}</p>}
  </div>;
}
